// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import type { IPublicClientApplication } from "@azure/msal-browser";

const INTERACTIVE_REAUTH_KEY = "riddle-interactive-reauth-attempted";

const INTERACTION_REQUIRED_CODES: readonly string[] = [
  "interaction_required",
  "consent_required",
  "login_required",
  "bad_token",
  "ui_not_allowed",
  "interrupted_user",
];

export function isInteractionRequired(error: unknown): boolean {
  const failure = error as
    | { errorCode?: string; errorMessage?: string; subError?: string }
    | null
    | undefined;
  const errorCode = failure?.errorCode ?? "";
  if (INTERACTION_REQUIRED_CODES.includes(errorCode)) {
    return true;
  }

  if ((failure?.subError ?? "").length > 0) {
    return true;
  }

  const message = failure?.errorMessage ?? "";
  return INTERACTION_REQUIRED_CODES.some((code) => message.includes(code));
}

export function hasAttemptedInteractiveReauth(): boolean {
  try {
    return window.sessionStorage.getItem(INTERACTIVE_REAUTH_KEY) !== null;
  } catch {
    return true;
  }
}

export function markInteractiveReauthAttempted(): void {
  try {
    window.sessionStorage.setItem(INTERACTIVE_REAUTH_KEY, "1");
  } catch {
  }
}

export function clearInteractiveReauthAttempt(): void {
  try {
    window.sessionStorage.removeItem(INTERACTIVE_REAUTH_KEY);
  } catch {
  }
}

let redirectInFlight = false;

export function isInteractiveRedirectInFlight(): boolean {
  return redirectInFlight;
}

export function __resetInteractiveReauthForTests(): void {
  redirectInFlight = false;
}

export function attemptInteractiveReauth(
  instance: IPublicClientApplication,
  account: unknown,
  request: Record<string, unknown>,
): boolean {
  if (hasAttemptedInteractiveReauth()) {
    return false;
  }

  markInteractiveReauthAttempted();
  redirectInFlight = true;
  void instance.loginRedirect({ ...request, account } as never);
  return true;
}

export function pendingInteractiveRedirect<T>(): Promise<T> {
  return new Promise<T>(() => {});
}